import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';

const CreateOrderModal = ({ onClose, onCreateOrder }) => {
  const [currentStep, setCurrentStep] = useState(1);
  const [orderData, setOrderData] = useState({
    customer: '',
    items: [],
    shippingAddress: {
      street: '',
      city: '',
      state: '',
      zipCode: '',
      country: 'United States'
    },
    billingAddress: {
      street: '',
      city: '',
      state: '',
      zipCode: '',
      country: 'United States'
    },
    shippingMethod: '',
    paymentMethod: '',
    notes: ''
  });

  const [selectedProduct, setSelectedProduct] = useState('');
  const [productQuantity, setProductQuantity] = useState(1);
  const [sameAsShipping, setSameAsShipping] = useState(true);

  // Mock data
  const customers = [
    { value: 'cust1', label: 'John Smith - john.smith@email.com' },
    { value: 'cust2', label: 'Sarah Johnson - sarah.j@email.com' },
    { value: 'cust3', label: 'Michael Brown - m.brown@email.com' },
    { value: 'cust4', label: 'Emily Davis - emily.davis@email.com' }
  ];

  const products = [
    { value: 'prod1', label: 'Wireless Headphones', price: 99.99, sku: 'WH-001' },
    { value: 'prod2', label: 'Smartphone Case', price: 24.99, sku: 'SC-002' },
    { value: 'prod3', label: 'Bluetooth Speaker', price: 79.99, sku: 'BS-003' },
    { value: 'prod4', label: 'USB-C Cable', price: 12.99, sku: 'UC-004' }
  ];

  const shippingMethods = [
    { value: 'standard', label: 'Standard Shipping (5-7 days) - $5.99' },
    { value: 'express', label: 'Express Shipping (2-3 days) - $12.99' },
    { value: 'overnight', label: 'Overnight Shipping (1 day) - $24.99' },
    { value: 'pickup', label: 'Store Pickup - Free' }
  ];

  const paymentMethods = [
    { value: 'credit_card', label: 'Credit Card' },
    { value: 'debit_card', label: 'Debit Card' },
    { value: 'paypal', label: 'PayPal' },
    { value: 'bank_transfer', label: 'Bank Transfer' },
    { value: 'cash_on_delivery', label: 'Cash on Delivery' }
  ];

  const stateOptions = [
    { value: 'CA', label: 'California' },
    { value: 'NY', label: 'New York' },
    { value: 'TX', label: 'Texas' },
    { value: 'FL', label: 'Florida' },
    { value: 'IL', label: 'Illinois' }
  ];

  const steps = [
    { id: 1, title: 'Customer & Items', icon: 'User' },
    { id: 2, title: 'Shipping Address', icon: 'MapPin' },
    { id: 3, title: 'Payment & Review', icon: 'CreditCard' }
  ];

  const addProduct = () => {
    if (selectedProduct && productQuantity > 0) {
      const product = products.find(p => p.value === selectedProduct);
      const existingItem = orderData.items.find(item => item.productId === selectedProduct);
      
      if (existingItem) {
        setOrderData({
          ...orderData,
          items: orderData.items.map(item =>
            item.productId === selectedProduct
              ? { ...item, quantity: item.quantity + productQuantity }
              : item
          )
        });
      } else {
        setOrderData({
          ...orderData,
          items: [...orderData.items, {
            productId: selectedProduct,
            name: product.label,
            price: product.price,
            sku: product.sku,
            quantity: productQuantity
          }]
        });
      }
      
      setSelectedProduct('');
      setProductQuantity(1);
    }
  };

  const removeProduct = (productId) => {
    setOrderData({
      ...orderData,
      items: orderData.items.filter(item => item.productId !== productId)
    });
  };

  const calculateSubtotal = () => {
    return orderData.items.reduce((sum, item) => sum + (item.price * item.quantity), 0);
  };

  const calculateShipping = () => {
    const shippingCosts = {
      standard: 5.99,
      express: 12.99,
      overnight: 24.99,
      pickup: 0
    };
    return shippingCosts[orderData.shippingMethod] || 0;
  };

  const calculateTax = () => {
    return calculateSubtotal() * 0.08; // 8% tax
  };

  const calculateTotal = () => {
    return calculateSubtotal() + calculateShipping() + calculateTax();
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  const handleNext = () => {
    if (currentStep < 3) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handlePrevious = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleSubmit = () => {
    const newOrder = {
      id: Date.now(),
      orderNumber: `ORD-${Date.now()}`,
      customerName: customers.find(c => c.value === orderData.customer)?.label.split(' - ')[0] || '',
      customerEmail: customers.find(c => c.value === orderData.customer)?.label.split(' - ')[1] || '',
      orderDate: new Date().toISOString(),
      status: 'pending',
      items: orderData.items,
      itemCount: orderData.items.reduce((sum, item) => sum + item.quantity, 0),
      subtotal: calculateSubtotal(),
      shipping: calculateShipping(),
      tax: calculateTax(),
      total: calculateTotal(),
      shippingAddress: orderData.shippingAddress,
      billingAddress: sameAsShipping ? orderData.shippingAddress : orderData.billingAddress,
      shippingMethod: shippingMethods.find(s => s.value === orderData.shippingMethod)?.label || '',
      paymentMethod: paymentMethods.find(p => p.value === orderData.paymentMethod)?.label || '',
      paymentStatus: 'pending',
      transactionId: `TXN-${Date.now()}`,
      trackingNumber: null,
      estimatedDelivery: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toLocaleDateString(),
      notes: orderData.notes,
      history: [
        {
          action: 'Order Created',
          description: 'Order was manually created',
          timestamp: new Date().toISOString(),
          user: 'Admin User',
          icon: 'Plus'
        }
      ]
    };

    onCreateOrder(newOrder);
    onClose();
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return (
          <div className="space-y-6">
            {/* Customer Selection */}
            <div>
              <Select
                label="Select Customer"
                placeholder="Choose a customer"
                options={customers}
                value={orderData.customer}
                onChange={(value) => setOrderData({ ...orderData, customer: value })}
                required
                searchable
              />
            </div>

            {/* Product Selection */}
            <div className="space-y-4">
              <h4 className="font-medium text-foreground">Add Products</h4>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="md:col-span-2">
                  <Select
                    placeholder="Select a product"
                    options={products}
                    value={selectedProduct}
                    onChange={setSelectedProduct}
                    searchable
                  />
                </div>
                
                <div className="flex space-x-2">
                  <Input
                    type="number"
                    placeholder="Qty"
                    value={productQuantity}
                    onChange={(e) => setProductQuantity(parseInt(e.target.value) || 1)}
                    min="1"
                    className="flex-1"
                  />
                  
                  <Button
                    variant="outline"
                    onClick={addProduct}
                    disabled={!selectedProduct}
                    iconName="Plus"
                  >
                    Add
                  </Button>
                </div>
              </div>

              {/* Selected Items */}
              {orderData.items.length > 0 && (
                <div className="space-y-2">
                  <h5 className="text-sm font-medium text-foreground">Selected Items:</h5>
                  {orderData.items.map((item, index) => (
                    <div key={index} className="flex items-center justify-between p-3 bg-muted/30 rounded-md">
                      <div>
                        <div className="font-medium text-foreground">{item.name}</div>
                        <div className="text-sm text-muted-foreground">
                          {formatCurrency(item.price)} × {item.quantity} = {formatCurrency(item.price * item.quantity)}
                        </div>
                      </div>
                      
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => removeProduct(item.productId)}
                      >
                        <Icon name="Trash2" size={16} />
                      </Button>
                    </div>
                  ))}
                  
                  <div className="text-right pt-2 border-t border-border">
                    <div className="font-semibold text-foreground">
                      Subtotal: {formatCurrency(calculateSubtotal())}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        );

      case 2:
        return (
          <div className="space-y-6">
            {/* Shipping Address */}
            <div>
              <h4 className="font-medium text-foreground mb-4 flex items-center space-x-2">
                <Icon name="MapPin" size={16} className="text-primary" />
                <span>Shipping Address</span>
              </h4>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="md:col-span-2">
                  <Input
                    label="Street Address"
                    value={orderData.shippingAddress.street}
                    onChange={(e) => setOrderData({
                      ...orderData,
                      shippingAddress: { ...orderData.shippingAddress, street: e.target.value }
                    })}
                    required
                  />
                </div>
                
                <Input
                  label="City"
                  value={orderData.shippingAddress.city}
                  onChange={(e) => setOrderData({
                    ...orderData,
                    shippingAddress: { ...orderData.shippingAddress, city: e.target.value }
                  })}
                  required
                />
                
                <Select
                  label="State"
                  options={stateOptions}
                  value={orderData.shippingAddress.state}
                  onChange={(value) => setOrderData({
                    ...orderData,
                    shippingAddress: { ...orderData.shippingAddress, state: value }
                  })}
                  required
                />
                
                <Input
                  label="ZIP Code"
                  value={orderData.shippingAddress.zipCode}
                  onChange={(e) => setOrderData({
                    ...orderData,
                    shippingAddress: { ...orderData.shippingAddress, zipCode: e.target.value }
                  })}
                  required
                />
                
                <Input
                  label="Country"
                  value={orderData.shippingAddress.country}
                  onChange={(e) => setOrderData({
                    ...orderData,
                    shippingAddress: { ...orderData.shippingAddress, country: e.target.value }
                  })}
                  required
                />
              </div>
            </div>

            {/* Billing Address */}
            <div>
              <div className="flex items-center space-x-3 mb-4">
                <input
                  type="checkbox"
                  id="sameAsShipping"
                  checked={sameAsShipping}
                  onChange={(e) => setSameAsShipping(e.target.checked)}
                  className="rounded border-border"
                />
                <label htmlFor="sameAsShipping" className="text-sm font-medium text-foreground">
                  Billing address same as shipping
                </label>
              </div>

              {!sameAsShipping && (
                <div>
                  <h4 className="font-medium text-foreground mb-4 flex items-center space-x-2">
                    <Icon name="CreditCard" size={16} className="text-primary" />
                    <span>Billing Address</span>
                  </h4>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="md:col-span-2">
                      <Input
                        label="Street Address"
                        value={orderData.billingAddress.street}
                        onChange={(e) => setOrderData({
                          ...orderData,
                          billingAddress: { ...orderData.billingAddress, street: e.target.value }
                        })}
                        required
                      />
                    </div>
                    
                    <Input
                      label="City"
                      value={orderData.billingAddress.city}
                      onChange={(e) => setOrderData({
                        ...orderData,
                        billingAddress: { ...orderData.billingAddress, city: e.target.value }
                      })}
                      required
                    />
                    
                    <Select
                      label="State"
                      options={stateOptions}
                      value={orderData.billingAddress.state}
                      onChange={(value) => setOrderData({
                        ...orderData,
                        billingAddress: { ...orderData.billingAddress, state: value }
                      })}
                      required
                    />
                    
                    <Input
                      label="ZIP Code"
                      value={orderData.billingAddress.zipCode}
                      onChange={(e) => setOrderData({
                        ...orderData,
                        billingAddress: { ...orderData.billingAddress, zipCode: e.target.value }
                      })}
                      required
                    />
                    
                    <Input
                      label="Country"
                      value={orderData.billingAddress.country}
                      onChange={(e) => setOrderData({
                        ...orderData,
                        billingAddress: { ...orderData.billingAddress, country: e.target.value }
                      })}
                      required
                    />
                  </div>
                </div>
              )}
            </div>

            {/* Shipping Method */}
            <div>
              <Select
                label="Shipping Method"
                placeholder="Select shipping method"
                options={shippingMethods}
                value={orderData.shippingMethod}
                onChange={(value) => setOrderData({ ...orderData, shippingMethod: value })}
                required
              />
            </div>
          </div>
        );

      case 3:
        return (
          <div className="space-y-6">
            {/* Payment Method */}
            <div>
              <Select
                label="Payment Method"
                placeholder="Select payment method"
                options={paymentMethods}
                value={orderData.paymentMethod}
                onChange={(value) => setOrderData({ ...orderData, paymentMethod: value })}
                required
              />
            </div>

            {/* Order Notes */}
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Order Notes (Optional)
              </label>
              <textarea
                value={orderData.notes}
                onChange={(e) => setOrderData({ ...orderData, notes: e.target.value })}
                placeholder="Add any special instructions or notes..."
                rows={3}
                className="w-full px-3 py-2 border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
              />
            </div>

            {/* Order Summary */}
            <div className="bg-muted/30 rounded-lg p-4">
              <h4 className="font-medium text-foreground mb-4">Order Summary</h4>
              
              <div className="space-y-3">
                {orderData.items.map((item, index) => (
                  <div key={index} className="flex justify-between text-sm">
                    <span className="text-foreground">{item.name} × {item.quantity}</span>
                    <span className="text-foreground">{formatCurrency(item.price * item.quantity)}</span>
                  </div>
                ))}
                
                <div className="border-t border-border pt-3 space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Subtotal:</span>
                    <span className="text-foreground">{formatCurrency(calculateSubtotal())}</span>
                  </div>
                  
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Shipping:</span>
                    <span className="text-foreground">{formatCurrency(calculateShipping())}</span>
                  </div>
                  
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Tax:</span>
                    <span className="text-foreground">{formatCurrency(calculateTax())}</span>
                  </div>
                  
                  <div className="flex justify-between text-lg font-semibold border-t border-border pt-2">
                    <span className="text-foreground">Total:</span>
                    <span className="text-foreground">{formatCurrency(calculateTotal())}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  const isStepValid = () => {
    switch (currentStep) {
      case 1:
        return orderData.customer && orderData.items.length > 0;
      case 2:
        return orderData.shippingAddress.street && 
               orderData.shippingAddress.city && 
               orderData.shippingAddress.state && 
               orderData.shippingAddress.zipCode &&
               orderData.shippingMethod;
      case 3:
        return orderData.paymentMethod;
      default:
        return false;
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-elevation-3 w-full max-w-4xl max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border">
          <div className="flex items-center space-x-3">
            <Icon name="Plus" size={24} className="text-primary" />
            <div>
              <h2 className="text-xl font-semibold text-foreground">Create New Order</h2>
              <p className="text-sm text-muted-foreground">Step {currentStep} of 3</p>
            </div>
          </div>
          
          <Button
            variant="ghost"
            size="icon"
            onClick={onClose}
          >
            <Icon name="X" size={20} />
          </Button>
        </div>

        {/* Progress Steps */}
        <div className="px-6 py-4 border-b border-border">
          <div className="flex items-center justify-between">
            {steps.map((step, index) => (
              <div key={step.id} className="flex items-center">
                <div className={`flex items-center space-x-2 ${
                  currentStep >= step.id ? 'text-primary' : 'text-muted-foreground'
                }`}>
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                    currentStep >= step.id 
                      ? 'bg-primary text-primary-foreground' 
                      : 'bg-muted text-muted-foreground'
                  }`}>
                    {currentStep > step.id ? (
                      <Icon name="Check" size={16} />
                    ) : (
                      <Icon name={step.icon} size={16} />
                    )}
                  </div>
                  <span className="text-sm font-medium">{step.title}</span>
                </div>
                
                {index < steps.length - 1 && (
                  <div className={`w-16 h-0.5 mx-4 ${
                    currentStep > step.id ? 'bg-primary' : 'bg-border'
                  }`} />
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[50vh]">
          {renderStepContent()}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-6 border-t border-border bg-muted/30">
          <Button
            variant="outline"
            onClick={currentStep === 1 ? onClose : handlePrevious}
            iconName={currentStep === 1 ? "X" : "ChevronLeft"}
            iconPosition="left"
          >
            {currentStep === 1 ? 'Cancel' : 'Previous'}
          </Button>
          
          <div className="flex items-center space-x-3">
            {currentStep < 3 ? (
              <Button
                variant="default"
                onClick={handleNext}
                disabled={!isStepValid()}
                iconName="ChevronRight"
                iconPosition="right"
              >
                {t("next")}
              </Button>
            ) : (
              <Button
                variant="default"
                onClick={handleSubmit}
                disabled={!isStepValid()}
                iconName="Check"
                iconPosition="left"
              >
                Create Order
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreateOrderModal;